import {
  Cell,
  Slice,
  Address,
  Builder,
  beginCell,
  ComputeError,
  TupleItem,
  TupleReader,
  Dictionary,
  contractAddress,
  ContractProvider,
  Sender,
  Contract,
  ContractABI,
  ABIType,
  ABIGetter,
  ABIReceiver,
  TupleBuilder,
  DictionaryValue,
} from "@ton/core";

export type StateInit = {
  $$type: "StateInit";
  code: Cell;
  data: Cell;
};

export function storeStateInit(src: StateInit) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeRef(src.code);
    b_0.storeRef(src.data);
  };
}

export function loadStateInit(slice: Slice) {
  const sc_0 = slice;
  const _code = sc_0.loadRef();
  const _data = sc_0.loadRef();
  return { $$type: "StateInit" as const, code: _code, data: _data };
}

function loadTupleStateInit(source: TupleReader) {
  const _code = source.readCell();
  const _data = source.readCell();
  return { $$type: "StateInit" as const, code: _code, data: _data };
}

function storeTupleStateInit(source: StateInit) {
  const builder = new TupleBuilder();
  builder.writeCell(source.code);
  builder.writeCell(source.data);
  return builder.build();
}

function dictValueParserStateInit(): DictionaryValue<StateInit> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeStateInit(src)).endCell());
    },
    parse: (src) => {
      return loadStateInit(src.loadRef().beginParse());
    },
  };
}

export type Context = {
  $$type: "Context";
  bounced: boolean;
  sender: Address;
  value: bigint;
  raw: Cell;
};

export function storeContext(src: Context) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeBit(src.bounced);
    b_0.storeAddress(src.sender);
    b_0.storeInt(src.value, 257);
    b_0.storeRef(src.raw);
  };
}

export function loadContext(slice: Slice) {
  const sc_0 = slice;
  const _bounced = sc_0.loadBit();
  const _sender = sc_0.loadAddress();
  const _value = sc_0.loadIntBig(257);
  const _raw = sc_0.loadRef();
  return {
    $$type: "Context" as const,
    bounced: _bounced,
    sender: _sender,
    value: _value,
    raw: _raw,
  };
}

function loadTupleContext(source: TupleReader) {
  const _bounced = source.readBoolean();
  const _sender = source.readAddress();
  const _value = source.readBigNumber();
  const _raw = source.readCell();
  return {
    $$type: "Context" as const,
    bounced: _bounced,
    sender: _sender,
    value: _value,
    raw: _raw,
  };
}

function storeTupleContext(source: Context) {
  const builder = new TupleBuilder();
  builder.writeBoolean(source.bounced);
  builder.writeAddress(source.sender);
  builder.writeNumber(source.value);
  builder.writeSlice(source.raw);
  return builder.build();
}

function dictValueParserContext(): DictionaryValue<Context> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeContext(src)).endCell());
    },
    parse: (src) => {
      return loadContext(src.loadRef().beginParse());
    },
  };
}

export type SendParameters = {
  $$type: "SendParameters";
  bounce: boolean;
  to: Address;
  value: bigint;
  mode: bigint;
  body: Cell | null;
  code: Cell | null;
  data: Cell | null;
};

export function storeSendParameters(src: SendParameters) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeBit(src.bounce);
    b_0.storeAddress(src.to);
    b_0.storeInt(src.value, 257);
    b_0.storeInt(src.mode, 257);
    if (src.body !== null && src.body !== undefined) {
      b_0.storeBit(true).storeRef(src.body);
    } else {
      b_0.storeBit(false);
    }
    if (src.code !== null && src.code !== undefined) {
      b_0.storeBit(true).storeRef(src.code);
    } else {
      b_0.storeBit(false);
    }
    if (src.data !== null && src.data !== undefined) {
      b_0.storeBit(true).storeRef(src.data);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadSendParameters(slice: Slice) {
  const sc_0 = slice;
  const _bounce = sc_0.loadBit();
  const _to = sc_0.loadAddress();
  const _value = sc_0.loadIntBig(257);
  const _mode = sc_0.loadIntBig(257);
  const _body = sc_0.loadBit() ? sc_0.loadRef() : null;
  const _code = sc_0.loadBit() ? sc_0.loadRef() : null;
  const _data = sc_0.loadBit() ? sc_0.loadRef() : null;
  return {
    $$type: "SendParameters" as const,
    bounce: _bounce,
    to: _to,
    value: _value,
    mode: _mode,
    body: _body,
    code: _code,
    data: _data,
  };
}

function loadTupleSendParameters(source: TupleReader) {
  const _bounce = source.readBoolean();
  const _to = source.readAddress();
  const _value = source.readBigNumber();
  const _mode = source.readBigNumber();
  const _body = source.readCellOpt();
  const _code = source.readCellOpt();
  const _data = source.readCellOpt();
  return {
    $$type: "SendParameters" as const,
    bounce: _bounce,
    to: _to,
    value: _value,
    mode: _mode,
    body: _body,
    code: _code,
    data: _data,
  };
}

function storeTupleSendParameters(source: SendParameters) {
  const builder = new TupleBuilder();
  builder.writeBoolean(source.bounce);
  builder.writeAddress(source.to);
  builder.writeNumber(source.value);
  builder.writeNumber(source.mode);
  builder.writeCell(source.body);
  builder.writeCell(source.code);
  builder.writeCell(source.data);
  return builder.build();
}

function dictValueParserSendParameters(): DictionaryValue<SendParameters> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSendParameters(src)).endCell());
    },
    parse: (src) => {
      return loadSendParameters(src.loadRef().beginParse());
    },
  };
}

export type Deploy = {
  $$type: "Deploy";
  queryId: bigint;
};

export function storeDeploy(src: Deploy) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2490013878, 32);
    b_0.storeUint(src.queryId, 64);
  };
}

export function loadDeploy(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2490013878) {
    throw Error("Invalid prefix");
  }
  const _queryId = sc_0.loadUintBig(64);
  return { $$type: "Deploy" as const, queryId: _queryId };
}

function loadTupleDeploy(source: TupleReader) {
  const _queryId = source.readBigNumber();
  return { $$type: "Deploy" as const, queryId: _queryId };
}

function storeTupleDeploy(source: Deploy) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  return builder.build();
}

function dictValueParserDeploy(): DictionaryValue<Deploy> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeDeploy(src)).endCell());
    },
    parse: (src) => {
      return loadDeploy(src.loadRef().beginParse());
    },
  };
}

export type DeployOk = {
  $$type: "DeployOk";
  queryId: bigint;
};

export function storeDeployOk(src: DeployOk) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2952335191, 32);
    b_0.storeUint(src.queryId, 64);
  };
}

export function loadDeployOk(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2952335191) {
    throw Error("Invalid prefix");
  }
  const _queryId = sc_0.loadUintBig(64);
  return { $$type: "DeployOk" as const, queryId: _queryId };
}

function loadTupleDeployOk(source: TupleReader) {
  const _queryId = source.readBigNumber();
  return { $$type: "DeployOk" as const, queryId: _queryId };
}

function storeTupleDeployOk(source: DeployOk) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  return builder.build();
}

function dictValueParserDeployOk(): DictionaryValue<DeployOk> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeDeployOk(src)).endCell());
    },
    parse: (src) => {
      return loadDeployOk(src.loadRef().beginParse());
    },
  };
}

export type FactoryDeploy = {
  $$type: "FactoryDeploy";
  queryId: bigint;
  cashback: Address;
};

export function storeFactoryDeploy(src: FactoryDeploy) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(1829761339, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.cashback);
  };
}

export function loadFactoryDeploy(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 1829761339) {
    throw Error("Invalid prefix");
  }
  const _queryId = sc_0.loadUintBig(64);
  const _cashback = sc_0.loadAddress();
  return {
    $$type: "FactoryDeploy" as const,
    queryId: _queryId,
    cashback: _cashback,
  };
}

function loadTupleFactoryDeploy(source: TupleReader) {
  const _queryId = source.readBigNumber();
  const _cashback = source.readAddress();
  return {
    $$type: "FactoryDeploy" as const,
    queryId: _queryId,
    cashback: _cashback,
  };
}

function storeTupleFactoryDeploy(source: FactoryDeploy) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.cashback);
  return builder.build();
}

function dictValueParserFactoryDeploy(): DictionaryValue<FactoryDeploy> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeFactoryDeploy(src)).endCell());
    },
    parse: (src) => {
      return loadFactoryDeploy(src.loadRef().beginParse());
    },
  };
}

export type ChangeOwner = {
  $$type: "ChangeOwner";
  queryId: bigint;
  newOwner: Address;
};

export function storeChangeOwner(src: ChangeOwner) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2174598809, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.newOwner);
  };
}

export function loadChangeOwner(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2174598809) {
    throw Error("Invalid prefix");
  }
  const _queryId = sc_0.loadUintBig(64);
  const _newOwner = sc_0.loadAddress();
  return {
    $$type: "ChangeOwner" as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function loadTupleChangeOwner(source: TupleReader) {
  const _queryId = source.readBigNumber();
  const _newOwner = source.readAddress();
  return {
    $$type: "ChangeOwner" as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function storeTupleChangeOwner(source: ChangeOwner) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.newOwner);
  return builder.build();
}

function dictValueParserChangeOwner(): DictionaryValue<ChangeOwner> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeChangeOwner(src)).endCell());
    },
    parse: (src) => {
      return loadChangeOwner(src.loadRef().beginParse());
    },
  };
}

export type ChangeOwnerOk = {
  $$type: "ChangeOwnerOk";
  queryId: bigint;
  newOwner: Address;
};

export function storeChangeOwnerOk(src: ChangeOwnerOk) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(846932810, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.newOwner);
  };
}

export function loadChangeOwnerOk(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 846932810) {
    throw Error("Invalid prefix");
  }
  const _queryId = sc_0.loadUintBig(64);
  const _newOwner = sc_0.loadAddress();
  return {
    $$type: "ChangeOwnerOk" as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function loadTupleChangeOwnerOk(source: TupleReader) {
  const _queryId = source.readBigNumber();
  const _newOwner = source.readAddress();
  return {
    $$type: "ChangeOwnerOk" as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function storeTupleChangeOwnerOk(source: ChangeOwnerOk) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.newOwner);
  return builder.build();
}

function dictValueParserChangeOwnerOk(): DictionaryValue<ChangeOwnerOk> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeChangeOwnerOk(src)).endCell());
    },
    parse: (src) => {
      return loadChangeOwnerOk(src.loadRef().beginParse());
    },
  };
}

export type NftTransfer = {
  $$type: "NftTransfer";
  query_id: bigint;
  new_owner: Address;
  response_destination: Address;
  custom_payload: Cell | null;
  forward_amount: bigint;
  forward_payload: Cell;
};

export function storeNftTransfer(src: NftTransfer) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(1607220500, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeAddress(src.new_owner);
    b_0.storeAddress(src.response_destination);
    if (src.custom_payload !== null && src.custom_payload !== undefined) {
      b_0.storeBit(true).storeRef(src.custom_payload);
    } else {
      b_0.storeBit(false);
    }
    b_0.storeCoins(src.forward_amount);
    b_0.storeBuilder(src.forward_payload.asBuilder());
  };
}

export function loadNftTransfer(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 1607220500) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _new_owner = sc_0.loadAddress();
  const _response_destination = sc_0.loadAddress();
  const _custom_payload = sc_0.loadBit() ? sc_0.loadRef() : null;
  const _forward_amount = sc_0.loadCoins();
  const _forward_payload = sc_0.asCell();
  return {
    $$type: "NftTransfer" as const,
    query_id: _query_id,
    new_owner: _new_owner,
    response_destination: _response_destination,
    custom_payload: _custom_payload,
    forward_amount: _forward_amount,
    forward_payload: _forward_payload,
  };
}

function loadTupleNftTransfer(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _new_owner = source.readAddress();
  const _response_destination = source.readAddress();
  const _custom_payload = source.readCellOpt();
  const _forward_amount = source.readBigNumber();
  const _forward_payload = source.readCell();
  return {
    $$type: "NftTransfer" as const,
    query_id: _query_id,
    new_owner: _new_owner,
    response_destination: _response_destination,
    custom_payload: _custom_payload,
    forward_amount: _forward_amount,
    forward_payload: _forward_payload,
  };
}

function storeTupleNftTransfer(source: NftTransfer) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeAddress(source.new_owner);
  builder.writeAddress(source.response_destination);
  builder.writeCell(source.custom_payload);
  builder.writeNumber(source.forward_amount);
  builder.writeSlice(source.forward_payload);
  return builder.build();
}

function dictValueParserNftTransfer(): DictionaryValue<NftTransfer> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeNftTransfer(src)).endCell());
    },
    parse: (src) => {
      return loadNftTransfer(src.loadRef().beginParse());
    },
  };
}

export type NftGetStaticData = {
  $$type: "NftGetStaticData";
  query_id: bigint;
};

export function storeNftGetStaticData(src: NftGetStaticData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(801842850, 32);
    b_0.storeUint(src.query_id, 64);
  };
}

export function loadNftGetStaticData(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 801842850) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  return { $$type: "NftGetStaticData" as const, query_id: _query_id };
}

function loadTupleNftGetStaticData(source: TupleReader) {
  const _query_id = source.readBigNumber();
  return { $$type: "NftGetStaticData" as const, query_id: _query_id };
}

function storeTupleNftGetStaticData(source: NftGetStaticData) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  return builder.build();
}

function dictValueParserNftGetStaticData(): DictionaryValue<NftGetStaticData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeNftGetStaticData(src)).endCell());
    },
    parse: (src) => {
      return loadNftGetStaticData(src.loadRef().beginParse());
    },
  };
}

export type NftReportStaticData = {
  $$type: "NftReportStaticData";
  query_id: bigint;
  index: bigint;
  collection: Address;
};

export function storeNftReportStaticData(src: NftReportStaticData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2339837749, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeInt(src.index, 257);
    b_0.storeAddress(src.collection);
  };
}

export function loadNftReportStaticData(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2339837749) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _index = sc_0.loadIntBig(257);
  const _collection = sc_0.loadAddress();
  return {
    $$type: "NftReportStaticData" as const,
    query_id: _query_id,
    index: _index,
    collection: _collection,
  };
}

function loadTupleNftReportStaticData(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _index = source.readBigNumber();
  const _collection = source.readAddress();
  return {
    $$type: "NftReportStaticData" as const,
    query_id: _query_id,
    index: _index,
    collection: _collection,
  };
}

function storeTupleNftReportStaticData(source: NftReportStaticData) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeNumber(source.index);
  builder.writeAddress(source.collection);
  return builder.build();
}

function dictValueParserNftReportStaticData(): DictionaryValue<NftReportStaticData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeNftReportStaticData(src)).endCell()
      );
    },
    parse: (src) => {
      return loadNftReportStaticData(src.loadRef().beginParse());
    },
  };
}

export type ProveOwnership = {
  $$type: "ProveOwnership";
  query_id: bigint;
  dest: Address;
  forward_payload: Cell;
  with_content: boolean;
};

export function storeProveOwnership(src: ProveOwnership) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(81711432, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeAddress(src.dest);
    b_0.storeRef(src.forward_payload);
    b_0.storeBit(src.with_content);
  };
}

export function loadProveOwnership(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 81711432) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _dest = sc_0.loadAddress();
  const _forward_payload = sc_0.loadRef();
  const _with_content = sc_0.loadBit();
  return {
    $$type: "ProveOwnership" as const,
    query_id: _query_id,
    dest: _dest,
    forward_payload: _forward_payload,
    with_content: _with_content,
  };
}

function loadTupleProveOwnership(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _dest = source.readAddress();
  const _forward_payload = source.readCell();
  const _with_content = source.readBoolean();
  return {
    $$type: "ProveOwnership" as const,
    query_id: _query_id,
    dest: _dest,
    forward_payload: _forward_payload,
    with_content: _with_content,
  };
}

function storeTupleProveOwnership(source: ProveOwnership) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeAddress(source.dest);
  builder.writeCell(source.forward_payload);
  builder.writeBoolean(source.with_content);
  return builder.build();
}

function dictValueParserProveOwnership(): DictionaryValue<ProveOwnership> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeProveOwnership(src)).endCell());
    },
    parse: (src) => {
      return loadProveOwnership(src.loadRef().beginParse());
    },
  };
}

export type OwnershipProof = {
  $$type: "OwnershipProof";
  query_id: bigint;
  item_id: bigint;
  owner: Address;
  data: Cell;
  revoked_at: bigint;
  content: Cell | null;
};

export function storeOwnershipProof(src: OwnershipProof) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(86296494, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeUint(src.item_id, 256);
    b_0.storeAddress(src.owner);
    b_0.storeRef(src.data);
    b_0.storeUint(src.revoked_at, 64);
    if (src.content !== null && src.content !== undefined) {
      b_0.storeBit(true).storeRef(src.content);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadOwnershipProof(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 86296494) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _item_id = sc_0.loadUintBig(256);
  const _owner = sc_0.loadAddress();
  const _data = sc_0.loadRef();
  const _revoked_at = sc_0.loadUintBig(64);
  const _content = sc_0.loadBit() ? sc_0.loadRef() : null;
  return {
    $$type: "OwnershipProof" as const,
    query_id: _query_id,
    item_id: _item_id,
    owner: _owner,
    data: _data,
    revoked_at: _revoked_at,
    content: _content,
  };
}

function loadTupleOwnershipProof(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _item_id = source.readBigNumber();
  const _owner = source.readAddress();
  const _data = source.readCell();
  const _revoked_at = source.readBigNumber();
  const _content = source.readCellOpt();
  return {
    $$type: "OwnershipProof" as const,
    query_id: _query_id,
    item_id: _item_id,
    owner: _owner,
    data: _data,
    revoked_at: _revoked_at,
    content: _content,
  };
}

function storeTupleOwnershipProof(source: OwnershipProof) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeNumber(source.item_id);
  builder.writeAddress(source.owner);
  builder.writeCell(source.data);
  builder.writeNumber(source.revoked_at);
  builder.writeCell(source.content);
  return builder.build();
}

function dictValueParserOwnershipProof(): DictionaryValue<OwnershipProof> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeOwnershipProof(src)).endCell());
    },
    parse: (src) => {
      return loadOwnershipProof(src.loadRef().beginParse());
    },
  };
}

export type RequestOwner = {
  $$type: "RequestOwner";
  query_id: bigint;
  dest: Address;
  forward_payload: Cell;
  with_content: boolean;
};

export function storeRequestOwner(src: RequestOwner) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(3502489578, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeAddress(src.dest);
    b_0.storeRef(src.forward_payload);
    b_0.storeBit(src.with_content);
  };
}

export function loadRequestOwner(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 3502489578) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _dest = sc_0.loadAddress();
  const _forward_payload = sc_0.loadRef();
  const _with_content = sc_0.loadBit();
  return {
    $$type: "RequestOwner" as const,
    query_id: _query_id,
    dest: _dest,
    forward_payload: _forward_payload,
    with_content: _with_content,
  };
}

function loadTupleRequestOwner(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _dest = source.readAddress();
  const _forward_payload = source.readCell();
  const _with_content = source.readBoolean();
  return {
    $$type: "RequestOwner" as const,
    query_id: _query_id,
    dest: _dest,
    forward_payload: _forward_payload,
    with_content: _with_content,
  };
}

function storeTupleRequestOwner(source: RequestOwner) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeAddress(source.dest);
  builder.writeCell(source.forward_payload);
  builder.writeBoolean(source.with_content);
  return builder.build();
}

function dictValueParserRequestOwner(): DictionaryValue<RequestOwner> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeRequestOwner(src)).endCell());
    },
    parse: (src) => {
      return loadRequestOwner(src.loadRef().beginParse());
    },
  };
}

export type OwnerInfo = {
  $$type: "OwnerInfo";
  query_id: bigint;
  item_id: bigint;
  initiator: Address;
  owner: Address;
  data: Cell;
  revoked_at: bigint;
  content: Cell | null;
};

export function storeOwnerInfo(src: OwnerInfo) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(232130531, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeUint(src.item_id, 256);
    b_0.storeAddress(src.initiator);
    b_0.storeAddress(src.owner);
    b_0.storeRef(src.data);
    b_0.storeUint(src.revoked_at, 64);
    if (src.content !== null && src.content !== undefined) {
      b_0.storeBit(true).storeRef(src.content);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadOwnerInfo(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 232130531) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _item_id = sc_0.loadUintBig(256);
  const _initiator = sc_0.loadAddress();
  const _owner = sc_0.loadAddress();
  const _data = sc_0.loadRef();
  const _revoked_at = sc_0.loadUintBig(64);
  const _content = sc_0.loadBit() ? sc_0.loadRef() : null;
  return {
    $$type: "OwnerInfo" as const,
    query_id: _query_id,
    item_id: _item_id,
    initiator: _initiator,
    owner: _owner,
    data: _data,
    revoked_at: _revoked_at,
    content: _content,
  };
}

function loadTupleOwnerInfo(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _item_id = source.readBigNumber();
  const _initiator = source.readAddress();
  const _owner = source.readAddress();
  const _data = source.readCell();
  const _revoked_at = source.readBigNumber();
  const _content = source.readCellOpt();
  return {
    $$type: "OwnerInfo" as const,
    query_id: _query_id,
    item_id: _item_id,
    initiator: _initiator,
    owner: _owner,
    data: _data,
    revoked_at: _revoked_at,
    content: _content,
  };
}

function storeTupleOwnerInfo(source: OwnerInfo) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeNumber(source.item_id);
  builder.writeAddress(source.initiator);
  builder.writeAddress(source.owner);
  builder.writeCell(source.data);
  builder.writeNumber(source.revoked_at);
  builder.writeCell(source.content);
  return builder.build();
}

function dictValueParserOwnerInfo(): DictionaryValue<OwnerInfo> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeOwnerInfo(src)).endCell());
    },
    parse: (src) => {
      return loadOwnerInfo(src.loadRef().beginParse());
    },
  };
}

export type SetValidator = {
  $$type: "SetValidator";
  pub_key: bigint;
};

export function storeSetValidator(src: SetValidator) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(632774787, 32);
    b_0.storeUint(src.pub_key, 256);
  };
}

export function loadSetValidator(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 632774787) {
    throw Error("Invalid prefix");
  }
  const _pub_key = sc_0.loadUintBig(256);
  return { $$type: "SetValidator" as const, pub_key: _pub_key };
}

function loadTupleSetValidator(source: TupleReader) {
  const _pub_key = source.readBigNumber();
  return { $$type: "SetValidator" as const, pub_key: _pub_key };
}

function storeTupleSetValidator(source: SetValidator) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.pub_key);
  return builder.build();
}

function dictValueParserSetValidator(): DictionaryValue<SetValidator> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSetValidator(src)).endCell());
    },
    parse: (src) => {
      return loadSetValidator(src.loadRef().beginParse());
    },
  };
}

export type SetData = {
  $$type: "SetData";
  content: Cell | null;
  set_score_exec_fee: bigint | null;
  item_reserve_balance: bigint | null;
  collection_reserve_balance: bigint | null;
};

export function storeSetData(src: SetData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2189193868, 32);
    if (src.content !== null && src.content !== undefined) {
      b_0.storeBit(true).storeRef(src.content);
    } else {
      b_0.storeBit(false);
    }
    if (
      src.set_score_exec_fee !== null &&
      src.set_score_exec_fee !== undefined
    ) {
      b_0.storeBit(true).storeCoins(src.set_score_exec_fee);
    } else {
      b_0.storeBit(false);
    }
    if (
      src.item_reserve_balance !== null &&
      src.item_reserve_balance !== undefined
    ) {
      b_0.storeBit(true).storeCoins(src.item_reserve_balance);
    } else {
      b_0.storeBit(false);
    }
    if (
      src.collection_reserve_balance !== null &&
      src.collection_reserve_balance !== undefined
    ) {
      b_0.storeBit(true).storeCoins(src.collection_reserve_balance);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadSetData(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2189193868) {
    throw Error("Invalid prefix");
  }
  const _content = sc_0.loadBit() ? sc_0.loadRef() : null;
  const _set_score_exec_fee = sc_0.loadBit() ? sc_0.loadCoins() : null;
  const _item_reserve_balance = sc_0.loadBit() ? sc_0.loadCoins() : null;
  const _collection_reserve_balance = sc_0.loadBit() ? sc_0.loadCoins() : null;
  return {
    $$type: "SetData" as const,
    content: _content,
    set_score_exec_fee: _set_score_exec_fee,
    item_reserve_balance: _item_reserve_balance,
    collection_reserve_balance: _collection_reserve_balance,
  };
}

function loadTupleSetData(source: TupleReader) {
  const _content = source.readCellOpt();
  const _set_score_exec_fee = source.readBigNumberOpt();
  const _item_reserve_balance = source.readBigNumberOpt();
  const _collection_reserve_balance = source.readBigNumberOpt();
  return {
    $$type: "SetData" as const,
    content: _content,
    set_score_exec_fee: _set_score_exec_fee,
    item_reserve_balance: _item_reserve_balance,
    collection_reserve_balance: _collection_reserve_balance,
  };
}

function storeTupleSetData(source: SetData) {
  const builder = new TupleBuilder();
  builder.writeCell(source.content);
  builder.writeNumber(source.set_score_exec_fee);
  builder.writeNumber(source.item_reserve_balance);
  builder.writeNumber(source.collection_reserve_balance);
  return builder.build();
}

function dictValueParserSetData(): DictionaryValue<SetData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSetData(src)).endCell());
    },
    parse: (src) => {
      return loadSetData(src.loadRef().beginParse());
    },
  };
}

export type Signature = {
  $$type: "Signature";
  seqno: bigint;
  valid_until: bigint;
  signed_bytes: Buffer;
};

export function storeSignature(src: Signature) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(src.seqno, 32);
    b_0.storeUint(src.valid_until, 32);
    b_0.storeBuffer(src.signed_bytes);
  };
}

export function loadSignature(slice: Slice) {
  const sc_0 = slice;
  const _seqno = sc_0.loadUintBig(32);
  const _valid_until = sc_0.loadUintBig(32);
  const _signed_bytes = sc_0.loadBuffer(64);
  return {
    $$type: "Signature" as const,
    seqno: _seqno,
    valid_until: _valid_until,
    signed_bytes: _signed_bytes,
  };
}

function loadTupleSignature(source: TupleReader) {
  const _seqno = source.readBigNumber();
  const _valid_until = source.readBigNumber();
  const _signed_bytes = source.readBuffer();
  return {
    $$type: "Signature" as const,
    seqno: _seqno,
    valid_until: _valid_until,
    signed_bytes: _signed_bytes,
  };
}

function storeTupleSignature(source: Signature) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.seqno);
  builder.writeNumber(source.valid_until);
  builder.writeBuffer(source.signed_bytes);
  return builder.build();
}

function dictValueParserSignature(): DictionaryValue<Signature> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSignature(src)).endCell());
    },
    parse: (src) => {
      return loadSignature(src.loadRef().beginParse());
    },
  };
}

export type ScoreData = {
  $$type: "ScoreData";
  price: bigint;
  user: Address;
  content: Cell;
  score: bigint;
  referrer: Address | null;
  ref_amount: bigint;
};

export function storeScoreData(src: ScoreData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeCoins(src.price);
    b_0.storeAddress(src.user);
    b_0.storeRef(src.content);
    b_0.storeUint(src.score, 32);
    b_0.storeAddress(src.referrer);
    b_0.storeCoins(src.ref_amount);
  };
}

export function loadScoreData(slice: Slice) {
  const sc_0 = slice;
  const _price = sc_0.loadCoins();
  const _user = sc_0.loadAddress();
  const _content = sc_0.loadRef();
  const _score = sc_0.loadUintBig(32);
  const _referrer = sc_0.loadMaybeAddress();
  const _ref_amount = sc_0.loadCoins();
  return {
    $$type: "ScoreData" as const,
    price: _price,
    user: _user,
    content: _content,
    score: _score,
    referrer: _referrer,
    ref_amount: _ref_amount,
  };
}

function loadTupleScoreData(source: TupleReader) {
  const _price = source.readBigNumber();
  const _user = source.readAddress();
  const _content = source.readCell();
  const _score = source.readBigNumber();
  const _referrer = source.readAddressOpt();
  const _ref_amount = source.readBigNumber();
  return {
    $$type: "ScoreData" as const,
    price: _price,
    user: _user,
    content: _content,
    score: _score,
    referrer: _referrer,
    ref_amount: _ref_amount,
  };
}

function storeTupleScoreData(source: ScoreData) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.price);
  builder.writeAddress(source.user);
  builder.writeCell(source.content);
  builder.writeNumber(source.score);
  builder.writeAddress(source.referrer);
  builder.writeNumber(source.ref_amount);
  return builder.build();
}

function dictValueParserScoreData(): DictionaryValue<ScoreData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeScoreData(src)).endCell());
    },
    parse: (src) => {
      return loadScoreData(src.loadRef().beginParse());
    },
  };
}

export type SetScoreParent = {
  $$type: "SetScoreParent";
  signature: Signature;
  score_data: ScoreData;
};

export function storeSetScoreParent(src: SetScoreParent) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(3899072791, 32);
    b_0.store(storeSignature(src.signature));
    const b_1 = new Builder();
    b_1.store(storeScoreData(src.score_data));
    b_0.storeRef(b_1.endCell());
  };
}

export function loadSetScoreParent(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 3899072791) {
    throw Error("Invalid prefix");
  }
  const _signature = loadSignature(sc_0);
  const sc_1 = sc_0.loadRef().beginParse();
  const _score_data = loadScoreData(sc_1);
  return {
    $$type: "SetScoreParent" as const,
    signature: _signature,
    score_data: _score_data,
  };
}

function loadTupleSetScoreParent(source: TupleReader) {
  const _signature = loadTupleSignature(source.readTuple());
  const _score_data = loadTupleScoreData(source.readTuple());
  return {
    $$type: "SetScoreParent" as const,
    signature: _signature,
    score_data: _score_data,
  };
}

function storeTupleSetScoreParent(source: SetScoreParent) {
  const builder = new TupleBuilder();
  builder.writeTuple(storeTupleSignature(source.signature));
  builder.writeTuple(storeTupleScoreData(source.score_data));
  return builder.build();
}

function dictValueParserSetScoreParent(): DictionaryValue<SetScoreParent> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSetScoreParent(src)).endCell());
    },
    parse: (src) => {
      return loadSetScoreParent(src.loadRef().beginParse());
    },
  };
}

export type SetScoreUser = {
  $$type: "SetScoreUser";
  score_data: ScoreData;
  item_index: bigint;
  reserve_balance: bigint;
};

export function storeSetScoreUser(src: SetScoreUser) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(549834415, 32);
    b_0.store(storeScoreData(src.score_data));
    b_0.storeUint(src.item_index, 64);
    const b_1 = new Builder();
    b_1.storeCoins(src.reserve_balance);
    b_0.storeRef(b_1.endCell());
  };
}

export function loadSetScoreUser(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 549834415) {
    throw Error("Invalid prefix");
  }
  const _score_data = loadScoreData(sc_0);
  const _item_index = sc_0.loadUintBig(64);
  const sc_1 = sc_0.loadRef().beginParse();
  const _reserve_balance = sc_1.loadCoins();
  return {
    $$type: "SetScoreUser" as const,
    score_data: _score_data,
    item_index: _item_index,
    reserve_balance: _reserve_balance,
  };
}

function loadTupleSetScoreUser(source: TupleReader) {
  const _score_data = loadTupleScoreData(source.readTuple());
  const _item_index = source.readBigNumber();
  const _reserve_balance = source.readBigNumber();
  return {
    $$type: "SetScoreUser" as const,
    score_data: _score_data,
    item_index: _item_index,
    reserve_balance: _reserve_balance,
  };
}

function storeTupleSetScoreUser(source: SetScoreUser) {
  const builder = new TupleBuilder();
  builder.writeTuple(storeTupleScoreData(source.score_data));
  builder.writeNumber(source.item_index);
  builder.writeNumber(source.reserve_balance);
  return builder.build();
}

function dictValueParserSetScoreUser(): DictionaryValue<SetScoreUser> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSetScoreUser(src)).endCell());
    },
    parse: (src) => {
      return loadSetScoreUser(src.loadRef().beginParse());
    },
  };
}

export type SetScoreItem = {
  $$type: "SetScoreItem";
  data: ScoreData;
  reserve_balance: bigint;
};

export function storeSetScoreItem(src: SetScoreItem) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2312732293, 32);
    b_0.store(storeScoreData(src.data));
    b_0.storeCoins(src.reserve_balance);
  };
}

export function loadSetScoreItem(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2312732293) {
    throw Error("Invalid prefix");
  }
  const _data = loadScoreData(sc_0);
  const _reserve_balance = sc_0.loadCoins();
  return {
    $$type: "SetScoreItem" as const,
    data: _data,
    reserve_balance: _reserve_balance,
  };
}

function loadTupleSetScoreItem(source: TupleReader) {
  const _data = loadTupleScoreData(source.readTuple());
  const _reserve_balance = source.readBigNumber();
  return {
    $$type: "SetScoreItem" as const,
    data: _data,
    reserve_balance: _reserve_balance,
  };
}

function storeTupleSetScoreItem(source: SetScoreItem) {
  const builder = new TupleBuilder();
  builder.writeTuple(storeTupleScoreData(source.data));
  builder.writeNumber(source.reserve_balance);
  return builder.build();
}

function dictValueParserSetScoreItem(): DictionaryValue<SetScoreItem> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSetScoreItem(src)).endCell());
    },
    parse: (src) => {
      return loadSetScoreItem(src.loadRef().beginParse());
    },
  };
}

export type ProxyDeployedRequest = {
  $$type: "ProxyDeployedRequest";
  seqno: bigint;
  score_data: ScoreData;
};

export function storeProxyDeployedRequest(src: ProxyDeployedRequest) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(1044729494, 32);
    b_0.storeUint(src.seqno, 32);
    b_0.store(storeScoreData(src.score_data));
  };
}

export function loadProxyDeployedRequest(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 1044729494) {
    throw Error("Invalid prefix");
  }
  const _seqno = sc_0.loadUintBig(32);
  const _score_data = loadScoreData(sc_0);
  return {
    $$type: "ProxyDeployedRequest" as const,
    seqno: _seqno,
    score_data: _score_data,
  };
}

function loadTupleProxyDeployedRequest(source: TupleReader) {
  const _seqno = source.readBigNumber();
  const _score_data = loadTupleScoreData(source.readTuple());
  return {
    $$type: "ProxyDeployedRequest" as const,
    seqno: _seqno,
    score_data: _score_data,
  };
}

function storeTupleProxyDeployedRequest(source: ProxyDeployedRequest) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.seqno);
  builder.writeTuple(storeTupleScoreData(source.score_data));
  return builder.build();
}

function dictValueParserProxyDeployedRequest(): DictionaryValue<ProxyDeployedRequest> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeProxyDeployedRequest(src)).endCell()
      );
    },
    parse: (src) => {
      return loadProxyDeployedRequest(src.loadRef().beginParse());
    },
  };
}

export type ProxyDeployedResponse = {
  $$type: "ProxyDeployedResponse";
  is_mint: boolean;
  score_data: ScoreData;
};

export function storeProxyDeployedResponse(src: ProxyDeployedResponse) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(1897541925, 32);
    b_0.storeBit(src.is_mint);
    b_0.store(storeScoreData(src.score_data));
  };
}

export function loadProxyDeployedResponse(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 1897541925) {
    throw Error("Invalid prefix");
  }
  const _is_mint = sc_0.loadBit();
  const _score_data = loadScoreData(sc_0);
  return {
    $$type: "ProxyDeployedResponse" as const,
    is_mint: _is_mint,
    score_data: _score_data,
  };
}

function loadTupleProxyDeployedResponse(source: TupleReader) {
  const _is_mint = source.readBoolean();
  const _score_data = loadTupleScoreData(source.readTuple());
  return {
    $$type: "ProxyDeployedResponse" as const,
    is_mint: _is_mint,
    score_data: _score_data,
  };
}

function storeTupleProxyDeployedResponse(source: ProxyDeployedResponse) {
  const builder = new TupleBuilder();
  builder.writeBoolean(source.is_mint);
  builder.writeTuple(storeTupleScoreData(source.score_data));
  return builder.build();
}

function dictValueParserProxyDeployedResponse(): DictionaryValue<ProxyDeployedResponse> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeProxyDeployedResponse(src)).endCell()
      );
    },
    parse: (src) => {
      return loadProxyDeployedResponse(src.loadRef().beginParse());
    },
  };
}

export type QueryScore = {
  $$type: "QueryScore";
  query_id: bigint;
  user: Address;
};

export function storeQueryScore(src: QueryScore) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(1789555454, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeAddress(src.user);
  };
}

export function loadQueryScore(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 1789555454) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _user = sc_0.loadAddress();
  return { $$type: "QueryScore" as const, query_id: _query_id, user: _user };
}

function loadTupleQueryScore(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _user = source.readAddress();
  return { $$type: "QueryScore" as const, query_id: _query_id, user: _user };
}

function storeTupleQueryScore(source: QueryScore) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeAddress(source.user);
  return builder.build();
}

function dictValueParserQueryScore(): DictionaryValue<QueryScore> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeQueryScore(src)).endCell());
    },
    parse: (src) => {
      return loadQueryScore(src.loadRef().beginParse());
    },
  };
}

export type QueryScoreProxy = {
  $$type: "QueryScoreProxy";
  query_id: bigint;
  destination: Address;
};

export function storeQueryScoreProxy(src: QueryScoreProxy) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(3893761339, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeAddress(src.destination);
  };
}

export function loadQueryScoreProxy(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 3893761339) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _destination = sc_0.loadAddress();
  return {
    $$type: "QueryScoreProxy" as const,
    query_id: _query_id,
    destination: _destination,
  };
}

function loadTupleQueryScoreProxy(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _destination = source.readAddress();
  return {
    $$type: "QueryScoreProxy" as const,
    query_id: _query_id,
    destination: _destination,
  };
}

function storeTupleQueryScoreProxy(source: QueryScoreProxy) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeAddress(source.destination);
  return builder.build();
}

function dictValueParserQueryScoreProxy(): DictionaryValue<QueryScoreProxy> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeQueryScoreProxy(src)).endCell());
    },
    parse: (src) => {
      return loadQueryScoreProxy(src.loadRef().beginParse());
    },
  };
}

export type QueryScoreProxyResponse = {
  $$type: "QueryScoreProxyResponse";
  query_id: bigint;
  score: bigint;
  user: Address;
  destination: Address;
};

export function storeQueryScoreProxyResponse(src: QueryScoreProxyResponse) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(3949093957, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeUint(src.score, 32);
    b_0.storeAddress(src.user);
    b_0.storeAddress(src.destination);
  };
}

export function loadQueryScoreProxyResponse(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 3949093957) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _score = sc_0.loadUintBig(32);
  const _user = sc_0.loadAddress();
  const _destination = sc_0.loadAddress();
  return {
    $$type: "QueryScoreProxyResponse" as const,
    query_id: _query_id,
    score: _score,
    user: _user,
    destination: _destination,
  };
}

function loadTupleQueryScoreProxyResponse(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _score = source.readBigNumber();
  const _user = source.readAddress();
  const _destination = source.readAddress();
  return {
    $$type: "QueryScoreProxyResponse" as const,
    query_id: _query_id,
    score: _score,
    user: _user,
    destination: _destination,
  };
}

function storeTupleQueryScoreProxyResponse(source: QueryScoreProxyResponse) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeNumber(source.score);
  builder.writeAddress(source.user);
  builder.writeAddress(source.destination);
  return builder.build();
}

function dictValueParserQueryScoreProxyResponse(): DictionaryValue<QueryScoreProxyResponse> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeQueryScoreProxyResponse(src)).endCell()
      );
    },
    parse: (src) => {
      return loadQueryScoreProxyResponse(src.loadRef().beginParse());
    },
  };
}

export type QueryScoreResponse = {
  $$type: "QueryScoreResponse";
  query_id: bigint;
  score: bigint;
  user: Address;
};

export function storeQueryScoreResponse(src: QueryScoreResponse) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(356313908, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeUint(src.score, 32);
    b_0.storeAddress(src.user);
  };
}

export function loadQueryScoreResponse(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 356313908) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _score = sc_0.loadUintBig(32);
  const _user = sc_0.loadAddress();
  return {
    $$type: "QueryScoreResponse" as const,
    query_id: _query_id,
    score: _score,
    user: _user,
  };
}

function loadTupleQueryScoreResponse(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _score = source.readBigNumber();
  const _user = source.readAddress();
  return {
    $$type: "QueryScoreResponse" as const,
    query_id: _query_id,
    score: _score,
    user: _user,
  };
}

function storeTupleQueryScoreResponse(source: QueryScoreResponse) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeNumber(source.score);
  builder.writeAddress(source.user);
  return builder.build();
}

function dictValueParserQueryScoreResponse(): DictionaryValue<QueryScoreResponse> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeQueryScoreResponse(src)).endCell()
      );
    },
    parse: (src) => {
      return loadQueryScoreResponse(src.loadRef().beginParse());
    },
  };
}

export type NftData = {
  $$type: "NftData";
  deployed: boolean;
  index: bigint;
  collection: Address;
  owner: Address;
  content: Cell;
};

export function storeNftData(src: NftData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeBit(src.deployed);
    b_0.storeInt(src.index, 257);
    b_0.storeAddress(src.collection);
    b_0.storeAddress(src.owner);
    b_0.storeRef(src.content);
  };
}

export function loadNftData(slice: Slice) {
  const sc_0 = slice;
  const _deployed = sc_0.loadBit();
  const _index = sc_0.loadIntBig(257);
  const _collection = sc_0.loadAddress();
  const _owner = sc_0.loadAddress();
  const _content = sc_0.loadRef();
  return {
    $$type: "NftData" as const,
    deployed: _deployed,
    index: _index,
    collection: _collection,
    owner: _owner,
    content: _content,
  };
}

function loadTupleNftData(source: TupleReader) {
  const _deployed = source.readBoolean();
  const _index = source.readBigNumber();
  const _collection = source.readAddress();
  const _owner = source.readAddress();
  const _content = source.readCell();
  return {
    $$type: "NftData" as const,
    deployed: _deployed,
    index: _index,
    collection: _collection,
    owner: _owner,
    content: _content,
  };
}

function storeTupleNftData(source: NftData) {
  const builder = new TupleBuilder();
  builder.writeBoolean(source.deployed);
  builder.writeNumber(source.index);
  builder.writeAddress(source.collection);
  builder.writeAddress(source.owner);
  builder.writeCell(source.content);
  return builder.build();
}

function dictValueParserNftData(): DictionaryValue<NftData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeNftData(src)).endCell());
    },
    parse: (src) => {
      return loadNftData(src.loadRef().beginParse());
    },
  };
}

export type CollectionData = {
  $$type: "CollectionData";
  next_index: bigint;
  content: Cell;
  owner: Address;
};

export function storeCollectionData(src: CollectionData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeInt(src.next_index, 257);
    b_0.storeRef(src.content);
    b_0.storeAddress(src.owner);
  };
}

export function loadCollectionData(slice: Slice) {
  const sc_0 = slice;
  const _next_index = sc_0.loadIntBig(257);
  const _content = sc_0.loadRef();
  const _owner = sc_0.loadAddress();
  return {
    $$type: "CollectionData" as const,
    next_index: _next_index,
    content: _content,
    owner: _owner,
  };
}

function loadTupleCollectionData(source: TupleReader) {
  const _next_index = source.readBigNumber();
  const _content = source.readCell();
  const _owner = source.readAddress();
  return {
    $$type: "CollectionData" as const,
    next_index: _next_index,
    content: _content,
    owner: _owner,
  };
}

function storeTupleCollectionData(source: CollectionData) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.next_index);
  builder.writeCell(source.content);
  builder.writeAddress(source.owner);
  return builder.build();
}

function dictValueParserCollectionData(): DictionaryValue<CollectionData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeCollectionData(src)).endCell());
    },
    parse: (src) => {
      return loadCollectionData(src.loadRef().beginParse());
    },
  };
}

type NomisScore_init_args = {
  $$type: "NomisScore_init_args";
  owner: Address;
  content: Cell;
  validator_pub_key: bigint;
};

function initNomisScore_init_args(src: NomisScore_init_args) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeAddress(src.owner);
    b_0.storeRef(src.content);
    b_0.storeInt(src.validator_pub_key, 257);
  };
}

async function NomisScore_init(
  owner: Address,
  content: Cell,
  validator_pub_key: bigint
) {
  const __code = Cell.fromBase64(
    "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"
  );
  const __system = Cell.fromBase64(
    "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"
  );
  const builder = beginCell();
  builder.storeRef(__system);
  builder.storeUint(0, 1);
  initNomisScore_init_args({
    $$type: "NomisScore_init_args",
    owner,
    content,
    validator_pub_key,
  })(builder);
  const __data = builder.endCell();
  return { code: __code, data: __data };
}

const NomisScore_errors: { [key: number]: { message: string } } = {
  2: { message: `Stack underflow` },
  3: { message: `Stack overflow` },
  4: { message: `Integer overflow` },
  5: { message: `Integer out of expected range` },
  6: { message: `Invalid opcode` },
  7: { message: `Type check error` },
  8: { message: `Cell overflow` },
  9: { message: `Cell underflow` },
  10: { message: `Dictionary error` },
  13: { message: `Out of gas error` },
  32: { message: `Method ID not found` },
  34: { message: `Action is invalid or not supported` },
  37: { message: `Not enough TON` },
  38: { message: `Not enough extra-currencies` },
  128: { message: `Null reference exception` },
  129: { message: `Invalid serialization prefix` },
  130: { message: `Invalid incoming message` },
  131: { message: `Constraints error` },
  132: { message: `Access denied` },
  133: { message: `Contract stopped` },
  134: { message: `Invalid argument` },
  135: { message: `Code of a contract was not found` },
  136: { message: `Invalid address` },
  137: { message: `Masterchain support is not enabled for this contract` },
  4429: { message: `Invalid sender` },
  5165: { message: `Not enough value` },
  17654: { message: `Invalid seqno` },
  17810: { message: `Invalid referrer addr/amount` },
  41207: { message: `invalid sender` },
  48401: { message: `Invalid signature` },
};

const NomisScore_types: ABIType[] = [
  {
    name: "StateInit",
    header: null,
    fields: [
      { name: "code", type: { kind: "simple", type: "cell", optional: false } },
      { name: "data", type: { kind: "simple", type: "cell", optional: false } },
    ],
  },
  {
    name: "Context",
    header: null,
    fields: [
      {
        name: "bounced",
        type: { kind: "simple", type: "bool", optional: false },
      },
      {
        name: "sender",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "value",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      { name: "raw", type: { kind: "simple", type: "slice", optional: false } },
    ],
  },
  {
    name: "SendParameters",
    header: null,
    fields: [
      {
        name: "bounce",
        type: { kind: "simple", type: "bool", optional: false },
      },
      {
        name: "to",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "value",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      {
        name: "mode",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      { name: "body", type: { kind: "simple", type: "cell", optional: true } },
      { name: "code", type: { kind: "simple", type: "cell", optional: true } },
      { name: "data", type: { kind: "simple", type: "cell", optional: true } },
    ],
  },
  {
    name: "Deploy",
    header: 2490013878,
    fields: [
      {
        name: "queryId",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
    ],
  },
  {
    name: "DeployOk",
    header: 2952335191,
    fields: [
      {
        name: "queryId",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
    ],
  },
  {
    name: "FactoryDeploy",
    header: 1829761339,
    fields: [
      {
        name: "queryId",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "cashback",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "ChangeOwner",
    header: 2174598809,
    fields: [
      {
        name: "queryId",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "newOwner",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "ChangeOwnerOk",
    header: 846932810,
    fields: [
      {
        name: "queryId",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "newOwner",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "NftTransfer",
    header: 1607220500,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "new_owner",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "response_destination",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "custom_payload",
        type: { kind: "simple", type: "cell", optional: true },
      },
      {
        name: "forward_amount",
        type: {
          kind: "simple",
          type: "uint",
          optional: false,
          format: "coins",
        },
      },
      {
        name: "forward_payload",
        type: {
          kind: "simple",
          type: "slice",
          optional: false,
          format: "remainder",
        },
      },
    ],
  },
  {
    name: "NftGetStaticData",
    header: 801842850,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
    ],
  },
  {
    name: "NftReportStaticData",
    header: 2339837749,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "index",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      {
        name: "collection",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "ProveOwnership",
    header: 81711432,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "dest",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "forward_payload",
        type: { kind: "simple", type: "cell", optional: false },
      },
      {
        name: "with_content",
        type: { kind: "simple", type: "bool", optional: false },
      },
    ],
  },
  {
    name: "OwnershipProof",
    header: 86296494,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "item_id",
        type: { kind: "simple", type: "uint", optional: false, format: 256 },
      },
      {
        name: "owner",
        type: { kind: "simple", type: "address", optional: false },
      },
      { name: "data", type: { kind: "simple", type: "cell", optional: false } },
      {
        name: "revoked_at",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: true },
      },
    ],
  },
  {
    name: "RequestOwner",
    header: 3502489578,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "dest",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "forward_payload",
        type: { kind: "simple", type: "cell", optional: false },
      },
      {
        name: "with_content",
        type: { kind: "simple", type: "bool", optional: false },
      },
    ],
  },
  {
    name: "OwnerInfo",
    header: 232130531,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "item_id",
        type: { kind: "simple", type: "uint", optional: false, format: 256 },
      },
      {
        name: "initiator",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "owner",
        type: { kind: "simple", type: "address", optional: false },
      },
      { name: "data", type: { kind: "simple", type: "cell", optional: false } },
      {
        name: "revoked_at",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: true },
      },
    ],
  },
  {
    name: "SetValidator",
    header: 632774787,
    fields: [
      {
        name: "pub_key",
        type: { kind: "simple", type: "uint", optional: false, format: 256 },
      },
    ],
  },
  {
    name: "SetData",
    header: 2189193868,
    fields: [
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: true },
      },
      {
        name: "set_score_exec_fee",
        type: { kind: "simple", type: "uint", optional: true, format: "coins" },
      },
      {
        name: "item_reserve_balance",
        type: { kind: "simple", type: "uint", optional: true, format: "coins" },
      },
      {
        name: "collection_reserve_balance",
        type: { kind: "simple", type: "uint", optional: true, format: "coins" },
      },
    ],
  },
  {
    name: "Signature",
    header: null,
    fields: [
      {
        name: "seqno",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "valid_until",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "signed_bytes",
        type: {
          kind: "simple",
          type: "fixed-bytes",
          optional: false,
          format: 64,
        },
      },
    ],
  },
  {
    name: "ScoreData",
    header: null,
    fields: [
      {
        name: "price",
        type: {
          kind: "simple",
          type: "uint",
          optional: false,
          format: "coins",
        },
      },
      {
        name: "user",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: false },
      },
      {
        name: "score",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "referrer",
        type: { kind: "simple", type: "address", optional: true },
      },
      {
        name: "ref_amount",
        type: {
          kind: "simple",
          type: "uint",
          optional: false,
          format: "coins",
        },
      },
    ],
  },
  {
    name: "SetScoreParent",
    header: 3899072791,
    fields: [
      {
        name: "signature",
        type: { kind: "simple", type: "Signature", optional: false },
      },
      {
        name: "score_data",
        type: { kind: "simple", type: "ScoreData", optional: false },
      },
    ],
  },
  {
    name: "SetScoreUser",
    header: 549834415,
    fields: [
      {
        name: "score_data",
        type: { kind: "simple", type: "ScoreData", optional: false },
      },
      {
        name: "item_index",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "reserve_balance",
        type: {
          kind: "simple",
          type: "uint",
          optional: false,
          format: "coins",
        },
      },
    ],
  },
  {
    name: "SetScoreItem",
    header: 2312732293,
    fields: [
      {
        name: "data",
        type: { kind: "simple", type: "ScoreData", optional: false },
      },
      {
        name: "reserve_balance",
        type: {
          kind: "simple",
          type: "uint",
          optional: false,
          format: "coins",
        },
      },
    ],
  },
  {
    name: "ProxyDeployedRequest",
    header: 1044729494,
    fields: [
      {
        name: "seqno",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "score_data",
        type: { kind: "simple", type: "ScoreData", optional: false },
      },
    ],
  },
  {
    name: "ProxyDeployedResponse",
    header: 1897541925,
    fields: [
      {
        name: "is_mint",
        type: { kind: "simple", type: "bool", optional: false },
      },
      {
        name: "score_data",
        type: { kind: "simple", type: "ScoreData", optional: false },
      },
    ],
  },
  {
    name: "QueryScore",
    header: 1789555454,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "user",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "QueryScoreProxy",
    header: 3893761339,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "destination",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "QueryScoreProxyResponse",
    header: 3949093957,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "score",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "user",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "destination",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "QueryScoreResponse",
    header: 356313908,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "score",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "user",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "NftData",
    header: null,
    fields: [
      {
        name: "deployed",
        type: { kind: "simple", type: "bool", optional: false },
      },
      {
        name: "index",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      {
        name: "collection",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "owner",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: false },
      },
    ],
  },
  {
    name: "CollectionData",
    header: null,
    fields: [
      {
        name: "next_index",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: false },
      },
      {
        name: "owner",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
];

const NomisScore_getters: ABIGetter[] = [
  {
    name: "get_next_index",
    arguments: [],
    returnType: { kind: "simple", type: "int", optional: false, format: 257 },
  },
  {
    name: "get_user_score_address",
    arguments: [
      {
        name: "user",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
    returnType: { kind: "simple", type: "address", optional: false },
  },
  {
    name: "get_collection_data",
    arguments: [],
    returnType: { kind: "simple", type: "CollectionData", optional: false },
  },
  {
    name: "get_nft_address_by_index",
    arguments: [
      {
        name: "index",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
    ],
    returnType: { kind: "simple", type: "address", optional: false },
  },
  {
    name: "get_nft_content",
    arguments: [
      {
        name: "index",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      {
        name: "individual_content",
        type: { kind: "simple", type: "cell", optional: false },
      },
    ],
    returnType: { kind: "simple", type: "cell", optional: false },
  },
  {
    name: "owner",
    arguments: [],
    returnType: { kind: "simple", type: "address", optional: false },
  },
];

const NomisScore_receivers: ABIReceiver[] = [
  { receiver: "internal", message: { kind: "typed", type: "SetValidator" } },
  { receiver: "internal", message: { kind: "typed", type: "SetData" } },
  { receiver: "internal", message: { kind: "typed", type: "SetScoreParent" } },
  {
    receiver: "internal",
    message: { kind: "typed", type: "ProxyDeployedResponse" },
  },
  { receiver: "internal", message: { kind: "typed", type: "QueryScore" } },
  {
    receiver: "internal",
    message: { kind: "typed", type: "QueryScoreProxyResponse" },
  },
  { receiver: "internal", message: { kind: "typed", type: "ChangeOwner" } },
  { receiver: "internal", message: { kind: "typed", type: "Deploy" } },
];

export class NomisScore implements Contract {
  static async init(owner: Address, content: Cell, validator_pub_key: bigint) {
    return await NomisScore_init(owner, content, validator_pub_key);
  }

  static async fromInit(
    owner: Address,
    content: Cell,
    validator_pub_key: bigint
  ) {
    const init = await NomisScore_init(owner, content, validator_pub_key);
    const address = contractAddress(0, init);
    return new NomisScore(address, init);
  }

  static fromAddress(address: Address) {
    return new NomisScore(address);
  }

  readonly address: Address;
  readonly init?: { code: Cell; data: Cell };
  readonly abi: ContractABI = {
    types: NomisScore_types,
    getters: NomisScore_getters,
    receivers: NomisScore_receivers,
    errors: NomisScore_errors,
  };

  public constructor(address: Address, init?: { code: Cell; data: Cell }) {
    this.address = address;
    this.init = init;
  }

  async send(
    provider: ContractProvider,
    via: Sender,
    args: { value: bigint; bounce?: boolean | null | undefined },
    message:
      | SetValidator
      | SetData
      | SetScoreParent
      | ProxyDeployedResponse
      | QueryScore
      | QueryScoreProxyResponse
      | ChangeOwner
      | Deploy
  ) {
    let body: Cell | null = null;
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "SetValidator"
    ) {
      body = beginCell().store(storeSetValidator(message)).endCell();
    }
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "SetData"
    ) {
      body = beginCell().store(storeSetData(message)).endCell();
    }
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "SetScoreParent"
    ) {
      body = beginCell().store(storeSetScoreParent(message)).endCell();
    }
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "ProxyDeployedResponse"
    ) {
      body = beginCell().store(storeProxyDeployedResponse(message)).endCell();
    }
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "QueryScore"
    ) {
      body = beginCell().store(storeQueryScore(message)).endCell();
    }
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "QueryScoreProxyResponse"
    ) {
      body = beginCell().store(storeQueryScoreProxyResponse(message)).endCell();
    }
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "ChangeOwner"
    ) {
      body = beginCell().store(storeChangeOwner(message)).endCell();
    }
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "Deploy"
    ) {
      body = beginCell().store(storeDeploy(message)).endCell();
    }
    if (body === null) {
      throw new Error("Invalid message type");
    }

    await provider.internal(via, { ...args, body });
  }

  async getGetNextIndex(provider: ContractProvider) {
    const builder = new TupleBuilder();
    const source = (await provider.get("get_next_index", builder.build()))
      .stack;
    const result = source.readBigNumber();
    return result;
  }

  async getGetUserScoreAddress(provider: ContractProvider, user: Address) {
    const builder = new TupleBuilder();
    builder.writeAddress(user);
    const source = (
      await provider.get("get_user_score_address", builder.build())
    ).stack;
    const result = source.readAddress();
    return result;
  }

  async getGetCollectionData(provider: ContractProvider) {
    const builder = new TupleBuilder();
    const source = (await provider.get("get_collection_data", builder.build()))
      .stack;
    const result = loadTupleCollectionData(source);
    return result;
  }

  async getGetNftAddressByIndex(provider: ContractProvider, index: bigint) {
    const builder = new TupleBuilder();
    builder.writeNumber(index);
    const source = (
      await provider.get("get_nft_address_by_index", builder.build())
    ).stack;
    const result = source.readAddress();
    return result;
  }

  async getGetNftContent(
    provider: ContractProvider,
    index: bigint,
    individual_content: Cell
  ) {
    const builder = new TupleBuilder();
    builder.writeNumber(index);
    builder.writeCell(individual_content);
    const source = (await provider.get("get_nft_content", builder.build()))
      .stack;
    const result = source.readCell();
    return result;
  }

  async getOwner(provider: ContractProvider) {
    const builder = new TupleBuilder();
    const source = (await provider.get("owner", builder.build())).stack;
    const result = source.readAddress();
    return result;
  }
}
